(() => {
  const drawerSelector = '.cart-drawer-modal';
  const visibleClass = 'cc-popup--visible';
  const giftMessageSelector = '[data-codex-gift-message]';
  const giftMessageAttribute = 'Gift message';
  const promoCtaSelector = '[data-codex-promo-cta]';
  const promoCtaLabel = 'Add second bag — 50% off';
  let giftMessageTimer;
  let giftMessageRequest = 0;
  let eligibleCartItemCount = null;
  let promoCartRequest = 0;
  let drawerWasOpen = false;
  let pendingDrawerReason = 'other';
  let pendingDrawerReasonTimer;
  let promoWasApplied = null;
  let lastSavedGiftMessage = null;
  let trackedGiftMessage = null;
  let offerViewedForOpen = false;

  const clearPendingDrawerReason = () => {
    window.clearTimeout(pendingDrawerReasonTimer);
    pendingDrawerReason = 'other';
  };

  const setPendingDrawerReason = (reason) => {
    window.clearTimeout(pendingDrawerReasonTimer);
    pendingDrawerReason = reason;
    pendingDrawerReasonTimer = window.setTimeout(clearPendingDrawerReason, 5000);
  };

  const syncDrawerAccessibility = (drawer) => {
    if (!drawer) return;

    const isOpen = drawer.classList.contains(visibleClass);
    drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    drawer.toggleAttribute('inert', !isOpen);

    if (isOpen) {
      const dialog = drawer.querySelector('[role="dialog"]');
      const closeButton = drawer.querySelector('.cc-popup-close');
      if (dialog && closeButton && window.theme && typeof window.theme.trapFocus === 'function') {
        window.theme.trapFocus(dialog, closeButton);
      }
    } else {
      if (window.theme && typeof window.theme.removeTrapFocus === 'function') {
        window.theme.removeTrapFocus();
      }
    }

    if (isOpen && !drawerWasOpen) {
      const reason = pendingDrawerReason;
      clearPendingDrawerReason();
      offerViewedForOpen = false;
      document.dispatchEvent(new CustomEvent('mochila:cart:drawer-open', {
        detail: { reason }
      }));
      window.requestAnimationFrame(syncCartAnalyticsState);
    } else if (!isOpen && drawerWasOpen) {
      offerViewedForOpen = false;
    }
    drawerWasOpen = isOpen;
  };

  const syncGiftMessageReference = (drawer) => {
    const giftMessage = drawer?.querySelector(giftMessageSelector) || null;
    if (giftMessage === trackedGiftMessage) return;
    trackedGiftMessage = giftMessage;
    lastSavedGiftMessage = giftMessage ? giftMessage.value : null;
  };

  const syncCartAnalyticsState = () => {
    const drawer = document.querySelector(drawerSelector);
    const state = document.querySelector('[data-codex-cart-state]');
    const promoIsApplied = Boolean(state && state.dataset.codexPromoApplied === 'true');
    if (promoWasApplied === false && promoIsApplied) {
      document.dispatchEvent(new CustomEvent('mochila:cart:promo-applied'));
    }
    promoWasApplied = promoIsApplied;

    const offerIsVisible = Boolean(
      drawer?.classList.contains(visibleClass) &&
      state?.dataset.codexEligibleCount === '1' &&
      !promoIsApplied
    );
    if (offerIsVisible && !offerViewedForOpen) {
      offerViewedForOpen = true;
      document.dispatchEvent(new CustomEvent('mochila:cart:second-bag-offer-view'));
    } else if (!offerIsVisible) {
      offerViewedForOpen = false;
    }

    syncGiftMessageReference(drawer);
  };

  const initialiseCartDrawer = () => {
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    syncDrawerAccessibility(drawer);

    const observer = new MutationObserver(() => syncDrawerAccessibility(drawer));
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    const cartStateObserver = new MutationObserver(syncCartAnalyticsState);
    cartStateObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['data-codex-eligible-count', 'data-codex-promo-applied', 'data-codex-gift-wrap'],
      childList: true,
      subtree: true
    });
    syncCartAnalyticsState();
  };

  const cartUrl = (suffix) => {
    const base = window.theme && window.theme.routes && window.theme.routes.cart_url
      ? window.theme.routes.cart_url
      : `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart`.replace('//cart', '/cart');

    return `${base}${suffix}`;
  };

  const setGiftMessageStatus = (textarea, message, isError = false) => {
    if (!textarea || !textarea.isConnected) return;

    const status = textarea.closest('.codex-cart-gift-message')?.querySelector('[data-codex-gift-message-status]');
    if (!status) return;

    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const saveGiftMessage = async (textarea, request) => {
    if (!textarea) return;

    window.clearTimeout(giftMessageTimer);
    const value = textarea.value;
    setGiftMessageStatus(textarea, 'Saving…');
    let failureStatus = 0;

    try {
      const response = await fetch(cartUrl('/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [giftMessageAttribute]: value } })
      });

      if (!response.ok) {
        failureStatus = response.status;
        throw new Error(`Cart update failed: ${response.status}`);
      }
      if (request === giftMessageRequest && textarea.value === value) {
        setGiftMessageStatus(textarea, value.trim() ? 'Saved' : '');
        if (value !== lastSavedGiftMessage) {
          const previousValue = lastSavedGiftMessage || '';
          const state = value.trim() ? previousValue.trim() ? 'updated' : 'added' : 'cleared';
          const length = value.trim().length;
          document.dispatchEvent(new CustomEvent('mochila:cart:gift-note-saved', {
            detail: { state, lengthBucket: length === 0 ? '0' : length <= 40 ? '1-40' : length <= 100 ? '41-100' : '101-plus' }
          }));
          lastSavedGiftMessage = value;
        }
      }
    } catch (error) {
      if (request === giftMessageRequest) {
        setGiftMessageStatus(textarea, 'Try again', true);
        document.dispatchEvent(new CustomEvent('mochila:ui-error', {
          detail: { component: 'gift-note', operation: 'save', kind: failureStatus ? 'http' : 'network', status: failureStatus }
        }));
      }
    }
  };

  const scheduleGiftMessageSave = (textarea) => {
    window.clearTimeout(giftMessageTimer);
    const request = ++giftMessageRequest;
    setGiftMessageStatus(textarea, 'Saving…');
    giftMessageTimer = window.setTimeout(() => saveGiftMessage(textarea, request), 450);
  };

  const clearOrphanedGiftMessage = async (cart) => {
    try {
      if (!cart) return;
      const hasGiftWrap = cart.items.some((item) => item.handle === 'gift-wrap');
      if (hasGiftWrap || !cart.attributes || !cart.attributes[giftMessageAttribute]) return;

      await fetch(cartUrl('/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [giftMessageAttribute]: '' } })
      });
    } catch (error) {
      // A stale gift message is harmless; a future cart interaction retries cleanup.
    }
  };

  const syncPromoCtas = () => {
    if (eligibleCartItemCount === null) return;

    const offerReady = eligibleCartItemCount === 1;
    document.querySelectorAll(promoCtaSelector).forEach((button) => {
      const baseLabel = button.dataset.codexBaseAddToCart || button.dataset.addToCartText || 'Add to cart';
      const nextLabel = offerReady ? promoCtaLabel : baseLabel;

      // The theme reads this value again after a variant selection changes.
      button.dataset.addToCartText = nextLabel;
      button.dataset.codexPromoEligible = offerReady ? 'true' : 'false';

      // Never replace sold-out, unavailable, or transient loading feedback.
      if (button.disabled || button.classList.contains('btn--in-progress')) return;
      button.textContent = nextLabel;
    });
  };

  const refreshCartState = async () => {
    const request = ++promoCartRequest;

    try {
      const response = await fetch(cartUrl('.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      if (request !== promoCartRequest) return;

      eligibleCartItemCount = cart.items.reduce((count, item) => {
        if (item.handle === 'gift-wrap') return count;
        return count + Number(item.quantity || 0);
      }, 0);
      syncPromoCtas();
      clearOrphanedGiftMessage(cart);

      syncCartAnalyticsState();
    } catch (error) {
      // Keep Shopify's standard label if cart state cannot be confirmed.
    }
  };

  document.addEventListener('input', (event) => {
    if (event.target.matches(giftMessageSelector)) scheduleGiftMessageSave(event.target);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches(giftMessageSelector)) {
      window.clearTimeout(giftMessageTimer);
      saveGiftMessage(event.target, ++giftMessageRequest);
    }
  });

  document.addEventListener('theme:cartchanged', () => {
    refreshCartState();
  });

  document.addEventListener('mochila:cart:add-success', () => {
    const drawer = document.querySelector(drawerSelector);
    if (drawer?.classList.contains(visibleClass)) clearPendingDrawerReason();
    else setPendingDrawerReason('add-success');
  });

  document.addEventListener('theme:open-cart-drawer', () => {
    const drawer = document.querySelector(drawerSelector);
    if (drawer?.classList.contains(visibleClass)) {
      clearPendingDrawerReason();
    } else if (pendingDrawerReason !== 'add-success') {
      setPendingDrawerReason('header-or-other');
    }
  });

  document.addEventListener('mochila:cart:refresh-error', () => {
    clearPendingDrawerReason();
  });

  document.addEventListener('on:variant:change', () => {
    window.requestAnimationFrame(syncPromoCtas);
  });

  document.addEventListener('shopify:section:load', () => {
    window.requestAnimationFrame(syncPromoCtas);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseCartDrawer();
      refreshCartState();
    }, { once: true });
  } else {
    initialiseCartDrawer();
    refreshCartState();
  }
})();
